// Membership → where the signed-in person stands (WEB_PLAN.md §3.1). One company per account (M4 single company),
// so the active membership wins; a suspended one alone means "Your access was paused"; none means No company.
// The decision and the SessionCtx it builds are pure (unit-tested); `fetchMemberships` calls the RPC.

import type { Membership, MemberStatus, Role } from '@/contracts/wire';
import { isUuid } from '@/contracts/uuid';
import type { SessionCtx } from '@/session/types';
import { callRpc } from './rpc';

const ROLES: readonly Role[] = ['admin', 'manager', 'rep'];

/** Swift `SupabaseSyncAPI.string`: a JSON string, or a number's text; anything else is missing. */
function jsonString(value: unknown): string | null {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

/** Swift `SupabaseSyncAPI.role`: case-insensitive; a missing or unknown role reads as the least privileged one. */
function parseRole(value: unknown): Role {
  const raw = typeof value === 'string' ? value.toLowerCase() : '';
  return (ROLES as readonly string[]).includes(raw) ? (raw as Role) : 'rep';
}

/**
 * Swift `Membership.status` (`?? "active"`, and `isActive` is `== "active"`): a missing status is active; any status
 * other than "active" is not, which is what "suspended" means here (the web has no third state).
 */
function parseStatus(value: unknown): MemberStatus {
  const raw = jsonString(value);
  if (raw === null) return 'active';
  return raw === 'active' ? 'active' : 'suspended';
}

/**
 * Decode of `my_memberships` (an array of `membership_json`) as the app does it (`SupabaseSyncAPI.membership`): an
 * entry needs a company id and a profile id that are UUIDs, or it is skipped; the rest has the app's defaults (an
 * unknown role from a newer server is a rep, never "no company", which would offer to create a second company).
 */
export function parseMemberships(json: unknown): Membership[] {
  const list = Array.isArray(json) ? json : [];
  const out: Membership[] = [];
  for (const entry of list) {
    if (typeof entry !== 'object' || entry === null || Array.isArray(entry)) continue;
    const m = entry as Record<string, unknown>;
    const companyId = typeof m.company_id === 'string' ? m.company_id.trim() : '';
    const profileId = typeof m.profile_id === 'string' ? m.profile_id.trim() : '';
    if (!isUuid(companyId) || !isUuid(profileId)) continue;
    out.push({
      company_id: companyId,
      company_name: jsonString(m.company_name),
      role: parseRole(m.role),
      profile_id: profileId,
      status: parseStatus(m.status),
      joined_at: typeof m.joined_at === 'string' ? m.joined_at : '',
      ai_enabled: m.ai_enabled === true,
    });
  }
  return out;
}

export type MembershipDecision =
  | { status: 'ready'; membership: Membership }
  | { status: 'suspended'; membership: Membership }
  | { status: 'noCompany' };

/** The first active membership (server order: joined_at); else the first suspended one; else none. */
export function decideMembership(list: Membership[]): MembershipDecision {
  const active = list.find((m) => m.status === 'active');
  if (active) return { status: 'ready', membership: active };
  const suspended = list.find((m) => m.status === 'suspended');
  if (suspended) return { status: 'suspended', membership: suspended };
  return { status: 'noCompany' };
}

/** Shown when the server has no name for the company (iOS falls back to "your team" / "My Company"). */
export const UNNAMED_COMPANY = 'Your company';

export function companyDisplayName(m: Pick<Membership, 'company_name'>): string {
  const name = m.company_name?.trim();
  return name ? name : UNNAMED_COMPANY;
}

/** SessionCtx for an active membership (company id lower-cased; profile id kept as the server sent it). */
export function sessionCtxFrom(user: { id: string; email?: string | null }, m: Membership): SessionCtx {
  const email = user.email?.trim();
  return {
    authUserId: user.id,
    email: email ? email : null,
    companyId: m.company_id.toLowerCase(),
    companyName: companyDisplayName(m),
    profileId: m.profile_id,
    role: m.role,
  };
}

export function sameSessionCtx(a: SessionCtx | null, b: SessionCtx | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.authUserId === b.authUserId && a.email === b.email && a.companyId === b.companyId
    && a.companyName === b.companyName && a.profileId === b.profileId && a.role === b.role;
}

/**
 * The data scope a browser's local replica belongs to. Local data is wiped whenever this changes: another account,
 * another company, or another role (a rep's feed is filtered, so a promoted rep must start over). Null = no company.
 */
export function scopeKey(ctx: Pick<SessionCtx, 'authUserId' | 'companyId' | 'role'> | null): string | null {
  return ctx ? `${ctx.authUserId}|${ctx.companyId}|${ctx.role}` : null;
}

/** `rpc('my_memberships')`. */
export async function fetchMemberships(): Promise<Membership[]> {
  return parseMemberships(await callRpc<unknown>('my_memberships'));
}
